import { useEffect, useState } from 'react'
import { notifyError } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * make a request via window.fetch and return the response as a hook return property
 *
 * @param url will make a GET request to this url on mount
 * @returns [response, error]
 */
export function useImmediateRequest<T>(
  url: string,
  showError = true,
): [T | undefined, any | undefined, () => Promise<void>] {
  const [response, setResponse] = useState<T>()
  const [responseError, setResponseError] = useState<any>()

  const getResponse = async () => {
    setResponse(undefined)
    if (url) {
      try {
        const res = await window.fetch(url)
        const data = await res.json()
        // Detect doorman-style error responses
        if (data && typeof data === `object` && `error` in data) {
          throw new Error(String((data as Record<string, unknown>).error))
        }
        setResponse(data as T)
      } catch (err) {
        logError(`Error response from server`, { url, err })
        if (showError) {
          notifyError(`There was a problem loading data from the server. You may need to reload the page.`)
        }
        setResponseError(err)
      }
    }
  }

  const triggerRevalidation = async () => {
    await getResponse()
  }

  useEffect(() => {
    bg(async () => getResponse())
  }, [url])

  return [response, responseError, triggerRevalidation]
}
